import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Pagination,
  Stack,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/auth.service.js';
import type { ClienteRow } from '../../hooks/useClientes.js';
import { ClienteDetailDialog } from './ClienteDetailDialog.js';

const PAGE_SIZE = 50;

// AIDEV-NOTE: Key exportada para invalidacao externa (ex.: apos cadastrar cliente na ClientesList)
export const CLIENTES_LISTA_QUERY_KEY = 'clientes-lista';

// AIDEV-NOTE: ClienteTable renders clients from API (/clientes) with debounced search + paginação
// Busca é debounced (400ms) para evitar query a cada tecla pressionada
export function ClienteTable() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [situacao] = useState<'A' | 'I' | ''>('A');
  const [page, setPage] = useState(1);
  const [selectedCodigo, setSelectedCodigo] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // AIDEV-NOTE: Debounce de 400ms — reseta para página 1 quando busca muda
  useEffect(() => {
    const timer = setTimeout(() => {
      setBuscaDebounced(busca);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [busca]);

  const { data, isLoading, isError } = useQuery({
    queryKey: [CLIENTES_LISTA_QUERY_KEY, buscaDebounced, situacao, page],
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<{ clientes: ClienteRow[]; total: number }> => {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE
      };
      if (buscaDebounced.trim()) params.q = buscaDebounced.trim();
      if (situacao) params.situacao = situacao;
      const res = await api.get<{ clientes: ClienteRow[]; total: number }>('/clientes', { params });
      return res.data;
    }
  });

  const clientes = data?.clientes ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (isError) return <Alert severity="error">Erro ao carregar clientes.</Alert>;

  const temFiltroAtivo = !!busca;

  return (
    <Box>
      {/* Barra de busca rápida + botão filtros (mobile) */}
      <Box display="flex" gap={1} mb={1.5} alignItems="center">
        <TextField
          size="small"
          placeholder="Buscar por código, nome, CNPJ, cidade..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        {isMobile && (
          <Button
            size="small"
            variant={temFiltroAtivo ? 'contained' : 'outlined'}
            onClick={() => setFiltrosAbertos((v) => !v)}
            sx={{ minWidth: 0, px: 1.5, flexShrink: 0 }}
          >
            <FilterListIcon fontSize="small" />
          </Button>
        )}
        {!isMobile && (
          <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
            {total} clientes
          </Typography>
        )}
      </Box>

      {/* Filtros adicionais — colapsáveis no mobile */}
      <Collapse in={!isMobile || filtrosAbertos}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={1.5}
          mb={1.5}
          alignItems="center"
        >
          {isMobile && (
            <Typography variant="body2" color="text.secondary">
              {total} clientes
            </Typography>
          )}
        </Box>
      </Collapse>

      {/* AIDEV-NOTE: overflowX auto — tabela de clientes pode ser estreita no mobile */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 360 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>
                <b>Cliente</b>
              </TableCell>
              <TableCell>
                <b>Cidade/UF</b>
              </TableCell>
              <TableCell align="right">
                <b>Ações</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientes.map((c) => (
              <TableRow key={c.codigo} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {c.razao_social}
                  </Typography>
                  {/* AIDEV-NOTE: codigo visivel — a busca aceita codigo do ERP */}
                  <Typography variant="caption" color="text.secondary">
                    Cód. {c.codigo} · {c.cnpj_cpf}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{c.cidade}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.uf}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedCodigo(c.codigo);
                      setDetailOpen(true);
                    }}
                    sx={{ mr: 1 }}
                  >
                    Ver
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {clientes.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    Nenhum cliente encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack alignItems="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            size="small"
          />
        </Stack>
      )}

      <ClienteDetailDialog
        clienteCodigo={selectedCodigo}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedCodigo(null);
        }}
      />
    </Box>
  );
}
